import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { useAuth } from "@/_core/hooks/useAuth";
import { getLoginUrl } from "@/const";
import VIPLayout from "@/components/VIPLayout";
import { vipDisplayName, VIP } from "@/lib/vipTheme";
import VipIcon, { type VipIconName } from "@/components/VipIcon";
import { ArrowRight } from "lucide-react";

// ─── Obsidian Blade ticket stat card ──────────────────────────────────────────
function StatCard({
  label,
  value,
  icon,
  accent,
  sub,
  urgent,
}: {
  label: string;
  value: string | number;
  icon: VipIconName;
  accent?: string;
  sub?: string;
  urgent?: boolean;
}) {
  return (
    <div className="vip-card" style={{ padding: "26px 24px" }}>
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          gap: "16px",
          position: "relative",
          zIndex: 1,
        }}
      >
        <div style={{ minWidth: 0 }}>
          <p
            style={{
              fontWeight: 600,
              fontSize: "11px",
              letterSpacing: "0.16em",
              textTransform: "uppercase",
              color: "rgba(26,26,25,0.72)",
              margin: "0 0 12px 0",
            }}
          >
            {label}
          </p>
          <p
            style={{
              fontWeight: 700,
              fontSize: "40px",
              color: accent || "#1A1A19",
              margin: 0,
              lineHeight: 1,
            }}
          >
            {value}
          </p>
          {sub && (
            <p
              style={{
                fontSize: "12px",
                color: "rgba(26,26,25,0.55)",
                margin: "10px 0 0 0",
                fontWeight: 400,
              }}
            >
              {sub}
            </p>
          )}
        </div>
        <VipIcon name={icon} size={36} tone={urgent ? "red" : "metal"} title={label} />
      </div>
    </div>
  );
}

// ─── Quick action button ──────────────────────────────────────────────────────
function QuickAction({
  label,
  icon,
  onClick,
  primary,
}: {
  label: string;
  icon: VipIconName;
  onClick: () => void;
  primary?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`vip-pill${primary ? " is-cta" : " is-ghost"}`}
      style={{
        padding: "14px 20px",
        fontSize: "12px",
        letterSpacing: "0.1em",
        flex: 1,
        minWidth: "140px",
        justifyContent: "center",
      }}
    >
      <VipIcon name={icon} size={16} tone={primary ? "white" : "metal"} />
      {label}
    </button>
  );
}

// ─── Activity type labels ─────────────────────────────────────────────────────
const ACTIVITY_LABELS: Record<string, { label: string; color: string }> = {
  lead_added: { label: "Lead Added", color: "#1E4D2B" },
  lead_moved: { label: "Deal Moved", color: "#8B5CF6" },
  note_added: { label: "Note Added", color: "#5B6660" },
  mod_started: { label: "Mod Started", color: "#F43F5E" },
  mod_completed: { label: "Strategy Generated", color: "#1E4D2B" },
  ai_query: { label: "AI Query", color: "#8B5CF6" },
  module_started: { label: "Module Started", color: "#5B6660" },
  module_completed: { label: "Module Completed", color: "#1A1A19" },
};

// ─── Checkout Success Modal ─────────────────────────────────────────────────
function CheckoutSuccessModal({ tier, onClose }: { tier: string; onClose: () => void }) {
  const TIER_NAMES: Record<string, string> = {
    vault: "VIP VAULT",
    ops: "OPS",
    enterprise: "ENTERPRISE",
    legacy: "LEGACY",
  };
  const tierName = TIER_NAMES[tier] ?? tier.toUpperCase();

  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        backgroundColor: "rgba(26,26,25,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        padding: "20px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          backgroundColor: "#FFFFFF",
          border: "1px solid rgba(30,77,43,0.5)",
          maxWidth: "520px",
          width: "100%",
          padding: "48px 40px",
          textAlign: "center",
          position: "relative",
          boxShadow: "0 0 60px rgba(30,77,43,0.15)",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Accent compass icon */}
        <div
          style={{
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            background: "radial-gradient(circle, rgba(30,77,43,0.3) 0%, rgba(30,77,43,0.03) 100%)",
            border: "2px solid rgba(30,77,43,0.6)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            margin: "0 auto 28px",
            fontSize: "32px",
          }}
        >
          🔑
        </div>

        <div
          style={{
            width: "48px",
            height: "2px",
            backgroundColor: "#1E4D2B",
            margin: "0 auto 20px",
          }}
        />

        <h2
          style={{
            fontWeight: 900,
            fontSize: "28px",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "#1A1A19",
            margin: "0 0 8px 0",
          }}
        >
          WELCOME TO THE VAULT
        </h2>
        <p
          style={{
            fontWeight: 700,
            fontSize: "13px",
            letterSpacing: "0.15em",
            color: "#1E4D2B",
            margin: "0 0 24px 0",
          }}
        >
          {tierName} ACCESS UNLOCKED
        </p>

        <p
          style={{
            fontSize: "15px",
            color: "#1A1A19",
            lineHeight: 1.7,
            margin: "0 0 32px 0",
          }}
        >
          You're in. The full system is now open. Start with the{" "}
          <strong style={{ color: "#1A1A19" }}>Dirty Dozen training modules</strong>, run your first{" "}
          <strong style={{ color: "#1A1A19" }}>Mod Analysis</strong>, or drop a deal into the{" "}
          <strong style={{ color: "#1A1A19" }}>Deal Tracker</strong>.
        </p>

        {/* Quick start actions — Design Port metal tickets */}
        <div style={{ display: "flex", gap: "10px", justifyContent: "center", flexWrap: "wrap", marginBottom: "28px" }}>
          {[
            { label: "START TRAINING", href: "/command/training", cta: true },
            { label: "DEAL TRACKER", href: "/command/deals", cta: false },
          ].map((item) => (
            <a
              key={item.label}
              href={item.href}
              onClick={() => onClose()}
              className={`vip-pill${item.cta ? " is-cta" : " is-ghost"}`}
              style={{ textDecoration: "none", padding: "10px 18px", fontSize: "11px" }}
            >
              {item.label}
            </a>
          ))}
        </div>

        <button
          onClick={onClose}
          style={{
            fontWeight: 700,
            fontSize: "11px",
            letterSpacing: "0.1em",
            color: "#1A1A19",
            background: "transparent",
            border: "none",
            cursor: "pointer",
          }}
        >
          CLOSE AND EXPLORE
        </button>
      </div>
    </div>
  );
}

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function VIPDashboard() {
  const navigate = (to: string) => {
    window.history.pushState({}, '', to);
    window.dispatchEvent(new PopStateEvent('popstate'));
  };
  const { user, loading, isAuthenticated } = useAuth();

  // Detect checkout success from URL params
  const [showWelcomeModal, setShowWelcomeModal] = useState(() => {
    if (typeof window === "undefined") return false;
    const params = new URLSearchParams(window.location.search);
    return params.get("checkout") === "success";
  });
  const [welcomeTier] = useState(() => {
    if (typeof window === "undefined") return "vault";
    const params = new URLSearchParams(window.location.search);
    return params.get("tier") ?? "vault";
  });

  const handleCloseModal = () => {
    setShowWelcomeModal(false);
    // Clean the URL without reloading
    const url = new URL(window.location.href);
    url.searchParams.delete("checkout");
    url.searchParams.delete("tier");
    window.history.replaceState({}, "", url.toString());
  };

  const { data: dealStats } = trpc.vipDeals.getDashboardStats.useQuery(undefined, {
    enabled: isAuthenticated,
  });
  const { data: modStats } = trpc.vipMod.getModCount.useQuery(undefined, {
    enabled: isAuthenticated,
  });
  const { data: activity } = trpc.vipActivity.getRecent.useQuery(undefined, {
    enabled: isAuthenticated,
  });
  const { data: opBrief } = trpc.vipAi.operatorBrief.useQuery(undefined, {
    enabled: isAuthenticated,
    staleTime: 60_000,
  });
  const seedFill = trpc.vipDemo.fill.useMutation();
  const seedClear = trpc.vipDemo.clear.useMutation();
  const utils = trpc.useUtils();

  if (loading) {
    return (
      <div
        style={{
          minHeight: "100vh",
          backgroundColor: "#FFFFFF",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <div
          style={{
            width: "40px",
            height: "40px",
            border: "3px solid #D9E2DC",
            borderTopColor: "#1E4D2B",
            borderRadius: "50%",
            animation: "spin 0.8s linear infinite",
          }}
        />
        <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
      </div>
    );
  }

  if (!isAuthenticated) {
    window.location.href = getLoginUrl();
    return null;
  }

  const daysUntilAuction = dealStats?.daysUntilNextAuction ?? null;
  const auctionColor =
    daysUntilAuction === null
      ? "#1E4D2B"
      : daysUntilAuction <= 21
      ? "#B91C1C"
      : daysUntilAuction <= 60
      ? "#F43F5E"
      : "#16A34A";

  return (
    <VIPLayout>
      {/* ─── Checkout Success Welcome Modal ─────────────────────────── */}
      {showWelcomeModal && (
        <CheckoutSuccessModal tier={welcomeTier} onClose={handleCloseModal} />
      )}

      {/* ─── Hero Welcome ─────────────────────────────────────────────── */}
      <div style={{ marginBottom: "40px" }}>
        <div
          style={{
            width: "48px",
            height: "3px",
            backgroundColor: "#1E4D2B",
            marginBottom: "16px",
          }}
        />
        <h1
          style={{
            fontWeight: 700,
            fontSize: "32px",
            letterSpacing: "0.08em",
            textTransform: "uppercase",
            color: "#1E4D2B",
            margin: "0 0 8px 0",
          }}
        >
          COMMAND CENTER
        </h1>
        <p
          style={{
            fontSize: "14px",
            color: "#1A1A19",
            margin: 0,
            fontWeight: 500,
            letterSpacing: "0.05em",
          }}
        >
          Your private command center for pre-foreclosure deal flow
          <span style={{ color: VIP.titleBlue }}> — {vipDisplayName(user)}</span>
        </p>
        {(user as { role?: string } | null)?.role === "admin" && (
          <div style={{ marginTop: "16px", display: "flex", gap: "10px", flexWrap: "wrap" }}>
            <button
              type="button"
              className="vip-pill is-cta"
              style={{ padding: "10px 16px", fontSize: "11px" }}
              disabled={seedFill.isPending}
              onClick={() => {
                seedFill.mutate(
                  { count: 100, clearFirst: true },
                  {
                    onSuccess: (r) => {
                      void utils.invalidate();
                      alert(
                        `Demo seed locked in:\nOP1 ${r.seeded.op1} · OP2 ${r.seeded.op2} · OP3 ${r.seeded.op3}\nDeals ${r.seeded.vipDeals} · Mods ${r.seeded.mods}\nVAs ${r.seeded.vas} · OP1 touches ${r.seeded.op1Touches}\nOP2 touches ${r.seeded.op2Touches} · OP3 convos ${r.seeded.op3Conversations}\n\nOpen OP1 / OP2 / OP3 command centers and hard-refresh.`
                      );
                    },
                    onError: (e) => alert(`Seed failed: ${e.message}`),
                  }
                );
              }}
            >
              {seedFill.isPending ? "SEEDING…" : "LOAD 100× OP DEMO DATA"}
            </button>
            <button
              type="button"
              className="vip-pill is-ghost"
              style={{ padding: "10px 16px", fontSize: "11px" }}
              disabled={seedClear.isPending}
              onClick={() => {
                seedClear.mutate(undefined, {
                  onSuccess: () => {
                    void utils.invalidate();
                    alert("DEMO-SEED rows cleared.");
                  },
                  onError: (e) => alert(`Clear failed: ${e.message}`),
                });
              }}
            >
              {seedClear.isPending ? "CLEARING…" : "CLEAR DEMO DATA"}
            </button>
          </div>
        )}
      </div>

      {/* ─── Stat Cards ───────────────────────────────────────────────── */}
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
          gap: "16px",
          marginBottom: "32px",
        }}
      >
        <StatCard
          label="Active Leads"
          value={dealStats?.activeLeads ?? 0}
          icon="active-leads"
          accent="#5B6660"
          sub="In pipeline"
        />
        <StatCard
          label="Deals Closed This Month"
          value={dealStats?.closedThisMonth ?? 0}
          icon="deals-closed"
          accent="#5B6660"
          sub="Won this month"
        />
        <StatCard
          label="Mods In Progress"
          value={modStats?.modsInProgress ?? 0}
          icon="mods-progress"
          accent="#5B6660"
          sub="Awaiting strategy"
        />
        <StatCard
          label="Days Until Next Auction"
          value={daysUntilAuction === null ? "—" : String(daysUntilAuction)}
          icon={daysUntilAuction !== null && daysUntilAuction <= 21 ? "auction-alert" : "auction-clock"}
          accent={auctionColor === "#1E4D2B" ? "#5B6660" : auctionColor}
          urgent={daysUntilAuction !== null && daysUntilAuction <= 21}
          sub={daysUntilAuction === null ? "No auctions scheduled" : "Earliest upcoming"}
        />
      </div>

      {/* ─── Operator Brief ───────────────────────────────────────────── */}
      <div className="vip-card w8-op-brief" style={{ padding: "24px 26px", marginBottom: "28px" }}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: "16px",
            flexWrap: "wrap",
            position: "relative",
            zIndex: 1,
          }}
        >
          <div style={{ minWidth: 0, flex: "1 1 240px" }}>
            <p
              style={{
                fontWeight: 700,
                fontSize: "10px",
                letterSpacing: "0.18em",
                textTransform: "uppercase",
                color: "rgba(26,26,25,0.7)",
                margin: "0 0 10px 0",
              }}
            >
              OPERATOR BRIEF
            </p>
            <p
              style={{
                fontWeight: 700,
                fontSize: "18px",
                color: "#1A1A19",
                margin: "0 0 14px 0",
                letterSpacing: "0.02em",
              }}
            >
              {opBrief?.summary ?? "Loading pipeline signal…"}
            </p>
            <div style={{ display: "flex", gap: "10px", flexWrap: "wrap", marginBottom: "14px" }}>
              {[
                { k: "Pipeline", v: opBrief?.activePipeline ?? "—" },
                { k: "Auction ≤14d", v: opBrief?.auctionsIn14d ?? "—" },
                { k: "Cold ≥72h", v: opBrief?.cold72h ?? "—" },
              ].map((x) => (
                <span
                  key={x.k}
                  className="vip-pill is-ghost"
                  style={{ padding: "6px 12px", minHeight: 28, fontSize: 10 }}
                >
                  {x.k}: <strong style={{ marginLeft: 4 }}>{x.v}</strong>
                </span>
              ))}
            </div>
            {opBrief?.hotDeals && opBrief.hotDeals.length > 0 && (
              <ul
                style={{
                  listStyle: "none",
                  margin: 0,
                  padding: 0,
                  display: "flex",
                  flexDirection: "column",
                  gap: 6,
                }}
              >
                {opBrief.hotDeals.slice(0, 3).map((h) => (
                  <li
                    key={h.id}
                    style={{
                      fontSize: 12,
                      color: "rgba(26,26,25,0.75)",
                      display: "flex",
                      gap: 8,
                      flexWrap: "wrap",
                    }}
                  >
                    <span style={{ color: "#1A1A19", fontWeight: 700 }}>{h.address}</span>
                    <span>· {h.stage}</span>
                    {h.daysToAuction != null && (
                      <span
                        style={{
                          color: h.daysToAuction <= 14 ? "#B91C1C" : "rgba(26,26,25,0.55)",
                          fontWeight: 700,
                        }}
                      >
                        {h.daysToAuction < 0
                          ? `PAST ${Math.abs(h.daysToAuction)}d`
                          : `${h.daysToAuction}d to auction`}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </div>
      </div>

      {/* ─── Quick Actions ─────────────────────────────────────────────── */}
      <div
        style={{
          backgroundColor: "rgba(30,77,43,0.02)",
          border: "1px solid #D9E2DC",
          borderRadius: "4px",
          padding: "24px",
          marginBottom: "32px",
        }}
      >
        <p
          style={{
            fontWeight: 700,
            fontSize: "10px",
            letterSpacing: "0.18em",
            textTransform: "uppercase",
            color: "#1A1A19",
            margin: "0 0 16px 0",
          }}
        >
          Quick Actions
        </p>
        <div
          style={{
            display: "flex",
            gap: "12px",
            flexWrap: "wrap",
          }}
        >
          <QuickAction
            label="ADD NEW LEAD"
            icon="add-lead"
            primary
            onClick={() => navigate("/command/deals?new=1")}
          />
          <QuickAction
            label="VIEW TRAINING"
            icon="training"
            onClick={() => navigate("/command/training")}
          />
        </div>
      </div>

      {/* ─── Recent Activity ───────────────────────────────────────────── */}
      <div
        style={{
          backgroundColor: "rgba(30,77,43,0.02)",
          border: "1px solid #D9E2DC",
          borderRadius: "4px",
          padding: "24px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "20px",
          }}
        >
          <p
            style={{
              fontWeight: 700,
              fontSize: "10px",
              letterSpacing: "0.18em",
              textTransform: "uppercase",
              color: "#1A1A19",
              margin: 0,
            }}
          >
            Recent Activity
          </p>
          <button
            onClick={() => navigate("/command/deal-tracker")}
            style={{
              background: "none",
              border: "none",
              color: "#1E4D2B",
              cursor: "pointer",
              fontSize: "11px",
              fontWeight: 600,
              letterSpacing: "0.1em",
              display: "flex",
              alignItems: "center",
              gap: "4px",
            }}
          >
            VIEW ALL <ArrowRight size={12} />
          </button>
        </div>

        {!activity || activity.length === 0 ? (
          <div
            style={{
              textAlign: "center",
              padding: "40px 0",
              color: "#5B6660",
              fontSize: "13px",
              fontWeight: 500,
            }}
          >
            No activity yet. Add your first lead to get started.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: "0" }}>
            {activity.map((item, idx) => {
              const meta = ACTIVITY_LABELS[item.activityType] ?? {
                label: item.activityType,
                color: "#1A1A19",
              };
              return (
                <div
                  key={item.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "16px",
                    padding: "14px 0",
                    borderBottom: idx < activity.length - 1 ? "1px solid #D9E2DC" : "none",
                  }}
                >
                  <div
                    style={{
                      width: "8px",
                      height: "8px",
                      borderRadius: "50%",
                      backgroundColor: meta.color,
                      flexShrink: 0,
                    }}
                  />
                  <div style={{ flex: 1 }}>
                    <span
                      style={{
                        fontSize: "11px",
                        fontWeight: 700,
                        color: meta.color,
                        letterSpacing: "0.08em",
                        textTransform: "uppercase",
                        marginRight: "8px",
                      }}
                    >
                      {meta.label}
                    </span>
                    <span
                      style={{
                        fontSize: "13px",
                        color: "#1A1A19",
                        fontWeight: 400,
                      }}
                    >
                      {item.description}
                    </span>
                  </div>
                  <span
                    style={{
                      fontSize: "11px",
                      color: "#5B6660",
                      fontWeight: 500,
                      flexShrink: 0,
                    }}
                  >
                    {new Date(item.createdAt).toLocaleString(undefined, {
                      month: "short",
                      day: "numeric",
                      hour: "2-digit",
                      minute: "2-digit",
                    })}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </VIPLayout>
  );
}
